// Web subscriptions go through Stripe Checkout, not RevenueCat: react-native-purchases
// is a native module and `purchases.ts` deliberately returns null on web. Without this,
// the Subscribe button on app.saddle-sites.com is a dead end that says "not available
// in this build yet" — which is true of IAP and irrelevant to a browser.
//
// The server owns the money: POST /billing/checkout mints a Stripe session bound to the
// caller's Cognito sub, and the signed webhook is what actually grants credits.
//
// Deliberately free of react-native: starting a checkout has nothing to do with the
// platform, so the decision of WHEN to use this lives at the call site and this module
// stays testable on its own — the same split as rateLimit.ts vs throttle.ts.

export interface WebCheckoutDeps {
  /** API Gateway base (not the LLM Function URL — billing lives on the gateway). */
  baseUrl: string;
  getToken: () => string | null;
  fetchFn?: typeof fetch;
  /** Navigate the browser. Injected so the redirect is observable in tests. */
  redirect?: (url: string) => void;
}

export type WebCheckoutResult =
  | { status: 'redirecting'; url: string }
  | { status: 'signin_required' }
  | { status: 'error'; message: string };

function defaultRedirect(url: string): void {
  if (typeof window !== 'undefined') window.location.assign(url);
}

export async function startWebCheckout(deps: WebCheckoutDeps): Promise<WebCheckoutResult> {
  const token = deps.getToken();
  // The endpoint would 401 anyway; checking first turns a dead button into a prompt
  // the person can actually act on.
  if (!token) return { status: 'signin_required' };

  const fetchFn = deps.fetchFn ?? fetch;
  const url = `${deps.baseUrl.replace(/\/+$/, '')}/billing/checkout`;

  let res: Response;
  try {
    res = await fetchFn(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
  } catch {
    return { status: 'error', message: 'Could not reach the server. Check your connection.' };
  }

  const body = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
  if (!res.ok) {
    return { status: 'error', message: body.error ?? 'Could not start checkout.' };
  }
  if (!body.url) {
    // Navigating to undefined would land the customer on a blank page mid-purchase.
    return { status: 'error', message: 'Could not start checkout.' };
  }

  (deps.redirect ?? defaultRedirect)(body.url);
  return { status: 'redirecting', url: body.url };
}
